import { FC, useMemo } from 'react'

import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { InputSelectControlField } from '@sequencehq/core-components'
import { HOURS } from '@sequencehq/utils/dates'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'

export type QuoteTemplateSelectorProps = {
  'data-testid'?: string
  value: string
  onChange: (value: string) => void
  validationErrors?: ValidationResult[]
  /** When true, the picker auto-opens on mount. */
  initiallyOpen?: boolean
  /**
   * Templates a sibling condition has already exhausted. Hidden from the list
   * so a row can't be built that says nothing new. The current `value` is never
   * hidden, so a stored condition still renders.
   */
  excludeIds?: string[]
}

/**
 * Quote-template picker for the QUOTE_TEMPLATE condition property. Archived
 * templates are excluded — a condition can't meaningfully compare against a
 * template the account has retired.
 */
export const QuoteTemplateSelector: FC<QuoteTemplateSelectorProps> = ({
  'data-testid': dataTestId,
  value,
  onChange,
  validationErrors,
  initiallyOpen,
  excludeIds
}) => {
  const { options } = useQuoteTemplateOptions()
  const selectableOptions = options.filter(
    option => option.value === value || !excludeIds?.includes(option.value)
  )

  return (
    <InputSelectControlField
      data-testid={dataTestId}
      matchWidth={false}
      value={value ?? ''}
      onChange={selected => onChange(String(selected))}
      options={selectableOptions}
      placeholder="Select template..."
      validationErrors={
        validationErrors?.map(error => ({ message: error.message })) ?? []
      }
      menuWidth={{ max: '320px', min: '100px' }}
      initiallyOpen={initiallyOpen}
    />
  )
}

export type QuoteTemplateOption = { label: string; value: string }

/**
 * The account's live quote templates as `{ label, value }` options, sorted by
 * title. Archived templates are excluded: a condition can't meaningfully
 * compare against a template the account has retired.
 *
 * Deliberately one page: accounts stay well under it, and only a plain query
 * can be warmed by `preloadQuoteTemplates`.
 */
export const useQuoteTemplateOptions = (): {
  options: QuoteTemplateOption[]
  titlesById: Map<string, string>
} => {
  const { data: templates } = useQuery(
    dashboard20240730Client.getListQuoteTemplates,
    undefined,
    {
      staleTime: 1 * HOURS,
      select: data => data?.items ?? []
    }
  )

  return useMemo(() => {
    const live = (templates ?? []).filter(template => !template.archivedAt)

    const options = live
      .map(template => ({
        label: template.title || template.id,
        value: template.id
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      )

    return {
      options,
      titlesById: new Map(options.map(option => [option.value, option.label]))
    }
  }, [templates])
}
